import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useGeneralStore } from '@/pinia'
import { useI18n } from 'vue-i18n'
import { useSelectionStore } from '@/pinia/selection'
import { useStore } from 'vuex'
import useMapbox from '@/composables/useMapbox'
import mapboxgl, { GeoJSONSourceSpecification } from 'mapbox-gl'

import {
  createColorCircleLabel,
  createDivider,
  createSmallSubtitle,
  createSmallTitle
} from '@/components/Builder'
import GeojsonLayer, {
  AnyLayerNotCustom,
  GeojsonLayerEvent
} from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import {
  getGroupedTicketsForEntity,
  GroupedResponseDto,
  TypeWithPriority
} from '@/services/tickets.service'
import { getPriorityColor } from '@/utils/ticketHelpers'
import MapPopupNew from '@/components/map/popups/MapPopupNew.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'LayerMarkerTickets',
  setup(__props) {

const piniaStore = useGeneralStore()
const selectionStore = useSelectionStore()
const store = useStore()
const { map } = useMapbox()
const { t } = useI18n()

let abortController: AbortController | null = null
const addedTicketLocations = ref<GeoJSON.Point[]>([])
const featuresTicketMarkers = ref<GeoJSON.Feature[]>([])
const hoveredMarkerId = ref<string | null>(null)
const idTicketMarkersLayer = ref<string>('ticket-markers-layer')
const idTicketMarkersSource = ref<string>('ticket-markers-source')
const popup = new mapboxgl.Popup({ closeButton: false, offset: 5 })
const popupRef = ref<HTMLElement | null>(null)
const popupSubtitle = ref('')
const popupTitle = ref('')
const popupTypes = ref<TypeWithPriority[]>([])

const { clickedEntityType } = storeToRefs(selectionStore)
const currentOverlayModalPinia = computed(() => {
  return piniaStore.currentOverlayModalPinia
})
const isMeasurementActive = computed<boolean>(() => store.getters.isMeasurementActive)
const selectedBuildingId = computed<string | undefined>(
  () => store.getters['mapview/selectedBuildingId']
)
const selectedObjectId = computed<string | null>(() => store.getters.selectedObjectId ?? null)
const selectedTicket = computed(() => piniaStore.selectedTicket)
const showOverlayModalPinia = computed(() => {
  return piniaStore.showOverlayModalPinia
})

const layerStyleMarkers = computed<AnyLayerNotCustom>(() => {
  return {
    id: idTicketMarkersLayer.value,
    source: idTicketMarkersSource.value,
    type: 'symbol',
    layout: {
      'icon-image': [
        'case',
        ['boolean', ['==', ['get', 'id'], markerWithSelectedTicket.value]],
        'Marker_round_yellow',
        ['boolean', ['==', ['get', 'highestPriority'], 'LOWEST'], false],
        'Marker_round_purple',
        ['boolean', ['==', ['get', 'highestPriority'], 'LOW'], false],
        'Marker_round_blue',
        ['boolean', ['==', ['get', 'highestPriority'], 'MEDIUM'], false],
        'Marker_round_orange',
        ['boolean', ['==', ['get', 'highestPriority'], 'HIGH'], false],
        'Marker_round_volcano',
        ['boolean', ['==', ['get', 'highestPriority'], 'HIGHEST'], false],
        'Marker_round_red',
        'Marker_round_gray'
      ],
      'icon-size': 1,
      'icon-allow-overlap': true,
      'icon-anchor': 'bottom',
      'text-field': '{countTickets}',
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-size': 18,
      'text-allow-overlap': true,
      'text-offset': [0, -1.15]
    },
    paint: {
      'text-color': [
        'case',
        ['boolean', ['==', ['get', 'id'], markerWithSelectedTicket.value]],
        'black',
        'white'
      ]
    }
  }
})
const markerWithSelectedTicket = computed(() => {
  if (!selectedTicket.value) return ''
  // Find the id for the the marker that contains the selected ticket
  const markerWithSelectedTicket = featuresTicketMarkers.value.find((feature) => {
    return feature.properties?.tickets.find((ticket) => ticket.id === selectedTicket.value?.id)
  })
  return markerWithSelectedTicket ? markerWithSelectedTicket.id : ''
})
const popupContent = computed(() => {
  let ticketTypes: any[] = []
  if (popupTypes.value.length) {
    for (let i = 0; i < popupTypes.value.length; i++) {
      if (i === popupTypes.value.length - 1) {
        // Don't add margin to the last element
        ticketTypes.push(
          createColorCircleLabel(
            getPriorityColor(popupTypes.value[i].highestPriority),
            popupTypes.value[i].type + ': ' + popupTypes.value[i].countTickets
          )
        )
      } else {
        ticketTypes.push(
          createColorCircleLabel(
            getPriorityColor(popupTypes.value[i].highestPriority),
            popupTypes.value[i].type + ': ' + popupTypes.value[i].countTickets,
            { marginBottom: '8px' }
          )
        )
      }
    }
  }
  return ticketTypes.length
    ? [
        createSmallTitle(popupTitle.value),
        createSmallSubtitle(popupSubtitle.value),
        createDivider(),
        ...ticketTypes
      ]
    : [createSmallTitle(popupTitle.value), createSmallSubtitle(popupSubtitle.value)]
})
const sourceTicketMarkers = computed<GeoJSONSourceSpecification>(() => {
  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: featuresTicketMarkers.value
    }
  }
})

async function generateTicketMarkers() {
  if (
    clickedEntityType.value === 'ZONE' ||
    clickedEntityType.value === 'LINE' ||
    clickedEntityType.value === 'POI'
  )
    return
  if (abortController) abortController.abort()
  abortController = new AbortController()
  featuresTicketMarkers.value = []
  addedTicketLocations.value = []
  if (clickedEntityType.value === 'REGION') return
  let ticketMarkerResponse: GroupedResponseDto[] | undefined | 'canceled' = undefined
  if (selectedObjectId.value?.length && clickedEntityType.value === 'COMPLEX') {
    ticketMarkerResponse = await getGroupedTicketsForEntity(
      selectedObjectId.value,
      'REAL_ESTATE',
      abortController.signal
    )
  } else if (
    selectedObjectId.value?.length &&
    (clickedEntityType.value === 'REAL_ESTATE' || clickedEntityType.value === 'CONSTRUCTION')
  ) {
    ticketMarkerResponse = await getGroupedTicketsForEntity(
      selectedObjectId.value,
      'CONSTRUCTION',
      abortController.signal
    )
  } else if (selectedBuildingId.value?.length && clickedEntityType.value === 'LEVEL') {
    ticketMarkerResponse = await getGroupedTicketsForEntity(
      selectedBuildingId.value,
      'CONSTRUCTION',
      abortController.signal
    )
  }
  if (ticketMarkerResponse !== 'canceled' && ticketMarkerResponse && ticketMarkerResponse.length) {
    let features: GeoJSON.Feature[] = ticketMarkerResponse
      .filter((ticketMarkersWithGeometry) => ticketMarkersWithGeometry.location !== null)
      .map((ticketMarker) => {
        return {
          type: 'Feature',
          id: ticketMarker.entityId + '-ticket-marker',
          properties: {
            countTickets: ticketMarker.summary.countTickets,
            entityName: ticketMarker.entityName,
            highestPriority: ticketMarker.summary.highestPriority,
            id: ticketMarker.entityId + '-ticket-marker',
            tickets: ticketMarker.tickets,
            type: ticketMarker.type,
            types: ticketMarker.summary.types
          },
          geometry: preventLocationOverlap(ticketMarker.location as GeoJSON.Point)
        }
      })
    if (clickedEntityType.value === 'CONSTRUCTION' || clickedEntityType.value === 'LEVEL') {
      features = features.filter((feature) => feature.properties!.type !== 'REAL_ESTATE')
    }
    featuresTicketMarkers.value = features
  }
}
function isLocationAdded(point: GeoJSON.Point): boolean {
  return addedTicketLocations.value.some((ticketLocation) => {
    return (
      ticketLocation.coordinates[0] === point.coordinates[0] &&
      ticketLocation.coordinates[1] === point.coordinates[1]
    )
  })
}

function handleMouseLeave(event: GeojsonLayerEvent) {
  if (isMeasurementActive.value) return
  setCursor('')
  updateHoverId(event)
}
function handleMouseMove(event: GeojsonLayerEvent) {
  updateHoverId(event)
}
function isFirstLayer(event: GeojsonLayerEvent) {
  const renderedFeatures = map?.queryRenderedFeatures(event.point)
  if (renderedFeatures == null || !renderedFeatures.length) return false
  return renderedFeatures[0].source === idTicketMarkersSource.value
}
function preventLocationOverlap(ticketLocation: GeoJSON.Point): GeoJSON.Point {
  while (isLocationAdded(ticketLocation)) {
    // ticketLocation.coordinates[0] += 0.000015
    ticketLocation.coordinates[1] += 0.00001
  }
  addedTicketLocations.value.push(ticketLocation)
  return ticketLocation
}
function resetHoverId() {
  hoveredMarkerId.value = null
  popup.remove()
}
function setCursor(cursorType: string) {
  if (isMeasurementActive.value) return
  if (map) map.getCanvas().style.cursor = cursorType
}
function showPopup(event: GeojsonLayerEvent) {
  if (!popupRef.value) return
  // @ts-ignore
  popup.setLngLat(event.lngLat).setDOMContent(popupRef.value.$el).addTo(map)
  popup.trackPointer()
}
function updateHoverId(event: GeojsonLayerEvent) {
  const features = event.features
  if (
    isMeasurementActive.value ||
    !isFirstLayer(event) ||
    !featuresTicketMarkers.value.length ||
    !features
  ) {
    resetHoverId()
    return
  }
  if (hoveredMarkerId.value === features[0].properties?.id) return
  hoveredMarkerId.value = features[0].properties?.id
  popupTitle.value =
    features[0].properties?.countTickets === 1
      ? features[0].properties?.countTickets + ' ' + t('general__data__ticket').toLowerCase()
      : features[0].properties?.countTickets + ' ' + t('general__label__tickets').toLowerCase()
  popupSubtitle.value = features[0].properties?.entityName ?? ''
  popupTypes.value = features[0].properties?.types ? JSON.parse(features[0].properties.types) : []
  showPopup(event)
}

watch(showOverlayModalPinia, () => {
  if (
    (currentOverlayModalPinia.value === 'createTicket' ||
      currentOverlayModalPinia.value === 'editTicket') &&
    !showOverlayModalPinia.value
  ) {
    generateTicketMarkers()
  }
})
watch(
  selectedObjectId,
  () => {
    generateTicketMarkers()
  },
  { immediate: true }
)
onBeforeUnmount(() => {
  if (abortController) abortController.abort()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (featuresTicketMarkers.value.length)
      ? (_openBlock(), _createBlock(GeojsonLayer, {
          key: 0,
          layer: layerStyleMarkers.value,
          source: sourceTicketMarkers.value,
          sourceId: idTicketMarkersSource.value,
          onLayerMouseEnter: _cache[0] || (_cache[0] = ($event: any) => (setCursor('pointer'))),
          onLayerMouseMove: handleMouseMove,
          onLayerMouseLeave: handleMouseLeave
        }, null, 8, ["layer", "source", "sourceId"]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(MapPopupNew, {
        ref_key: "popupRef",
        ref: popupRef,
        rows: popupContent.value
      }, null, 8, ["rows"])
    ], 512), [
      [_vShow, false]
    ])
  ], 64))
}
}

})